// React
import React, { useMemo } from "react";
// Utils
import { getFormattedNumber } from "helpers/general";
import { getTimeOnFarm } from "helpers/myLivestock";
import { GetAnimalById_animal } from "api/MyLivestock/Animal/types/GetAnimalById";
import { useGetCurrentBusinessUnit } from "hooks";
// Components
import { Flex, KeyValuePair, Paper, Spacer, Title } from "components";
import { AnimalsPerformancePill } from "components/MyLivestock";
// Types
import { KeyValuePairProps } from "components/Common/KeyValuePair";

interface AnimalPerformanceBlockProps {
  animal: GetAnimalById_animal;
  isArchived: boolean;
  showHeading?: boolean;
}

interface AnimalPerformanceItems extends KeyValuePairProps {
  isHidden?: boolean;
}

export const AnimalPerformanceBlock: React.FC<AnimalPerformanceBlockProps> = ({
  animal: {
    id,
    currentWeight,
    performanceCategory,
    dateMovedToFarm,
    growthRate,
    lastWeight,
    targetDailyLiveWeightGain,
    group,
    daysToTargetWeight,
  },
  isArchived = false,
  showHeading,
}) => {
  const { weightUnits } = useGetCurrentBusinessUnit();

  const performanceInfo: AnimalPerformanceItems[] = useMemo(
    () => [
      {
        isHidden: isArchived,
        label: "Current estimated weight",
        unit: weightUnits,
        value: currentWeight ? getFormattedNumber(currentWeight) : null,
      },
      {
        label: "Last weight",
        unit: weightUnits,
        value: lastWeight?.value ? getFormattedNumber(lastWeight?.value) : null,
      },
      {
        label: "DLWG",
        unit: `${weightUnits}/day`,
        value: growthRate ? getFormattedNumber(growthRate) : null,
      },
      {
        isHidden: isArchived,
        label: "Target DLWG",
        unit: `${weightUnits}/day`,
        value: targetDailyLiveWeightGain ? getFormattedNumber(targetDailyLiveWeightGain) : null,
      },
      {
        isHidden: isArchived,
        label: "Days Till Target Date",
        value: group?.daysTillTarget,
      },
      {
        isHidden: isArchived,
        label: "Days To Target Weight",
        value: daysToTargetWeight,
      },
      {
        isHidden: !dateMovedToFarm,
        label: "Time on farm",
        value: getTimeOnFarm(dateMovedToFarm),
      },
    ],
    [id],
  );

  const showPerformancePill = !isArchived && !!performanceCategory;

  return (
    <Paper>
      <Spacer baselineHeight={2} />
      <Flex container containerAlignItems="center" containerWrap="nowrap">
        {showHeading && (
          <Flex item itemGutter itemNoShrink>
            <Title secondary>Performance</Title>
          </Flex>
        )}
        <Flex item itemGutter itemGrow container>
          {showPerformancePill && (
            <Flex item itemGutter container containerAlignItems="center">
              <AnimalsPerformancePill
                label={`${performanceCategory} performance`.toLocaleUpperCase()}
                performanceCategory={performanceCategory}
              />
            </Flex>
          )}

          {performanceInfo.map(
            (item) =>
              !item?.isHidden && (
                <Flex key={item?.label} item itemGutter container containerAlignItems="center">
                  <KeyValuePair label={item?.label} unit={item?.unit} value={item?.value} />
                </Flex>
              ),
          )}
        </Flex>
      </Flex>
      <Spacer baselineHeight={2} />
    </Paper>
  );
};
